import { useEffect } from 'react';
import { useGcsStore } from '../../store/useGcsStore';
import { useNotificationStore } from '../../store/useNotificationStore';
import { useFleetVisibilityStore } from '../../store/useFleetVisibilityStore';
import { useSelectedVehicleStore } from '../../store/useSelectedVehicleStore';
import { selectIsVehicleUnderMyControl } from '../../store/selectors/selectIsVehicleUnderMyControl';
import { MissionFailureMode } from '../../types/gcs/mission';
import { DEFAULT_NOTIFICATION_DURATION_MS, InAppNotificationType } from '../../types/ui/inAppNotification';
import { en } from '../../locales/en';
import type { GcsStore } from '../../store/useGcsStore';
import type { VehicleId } from '../../types/gcs/vehicle';
import { soundEffectsService } from '../../services/sound/soundEffectsService';
import { getShortVehicleId } from '../../utils/formatters';
import { Countdown } from '../../components/general/Countdown';
import failureModeRtlSound from '../../assets/sounds/failure-mode-rtl.mp3';
import failureModeSafeUpSound from '../../assets/sounds/failure-mode-safe-up.mp3';
import failureModeDescendingSound from '../../assets/sounds/failure-mode-descending.mp3';
import failureModeEmergencyLandingSound from '../../assets/sounds/failure-mode-emergency-landing.mp3';
import failureModeFcLossSound from '../../assets/sounds/failure-mode-fc-loss.mp3';
import failureModeFtsTriggeredSound from '../../assets/sounds/failure-mode-fts-triggered.mp3';
import failureModeUnknownVehicleDisconnectedSound from '../../assets/sounds/failure-mode-unknown-vehicle-disconnected.mp3';
import failureModeExitVehicleReconnectedSound from '../../assets/sounds/failure-mode-exit-vehicle-reconnected.mp3';

interface GcsVehicleFailureModeListenerProps {
  gcsId: string;
}

export function GcsVehicleFailureModeListener({ gcsId }: GcsVehicleFailureModeListenerProps) {
  useEffect(() => {
    // Track dismiss callbacks for active failure mode notifications
    const dismissCallbacks = new Map<VehicleId, () => void>();

    const selectMissionFailureModes = (state: GcsStore): Map<VehicleId, MissionFailureMode> => {
      const failureModes = new Map<VehicleId, MissionFailureMode>();
      const gcsData = state.data.get(gcsId);

      if (gcsData) {
        gcsData.liveMissionsMap.forEach((mission, vehicleId) => {
          const missionState = gcsData.missionDetailedStateMap.get(mission.missionId);
          if (missionState) {
            failureModes.set(vehicleId, missionState.failureMode);
          }
        });
      }

      return failureModes;
    };

    // Custom equality function to compare Map contents
    const equalityFn = (a: Map<VehicleId, MissionFailureMode>, b: Map<VehicleId, MissionFailureMode>): boolean => {
      if (a.size !== b.size) return false;
      for (const [key, value] of a) {
        if (b.get(key) !== value) return false;
      }
      return true;
    };

    const unsubscribe = useGcsStore.subscribe(
      selectMissionFailureModes,
      (nextFailureModes, prevFailureModes = new Map()) => {
        const showNotification = useNotificationStore.getState().showNotification;
        const selectVehicle = useSelectedVehicleStore.getState().selectVehicle;
        const gcsData = useGcsStore.getState().data.get(gcsId);

        // Dismiss notifications for vehicles that are no longer in failure mode
        prevFailureModes.forEach((_, vehicleId) => {
          if (!nextFailureModes.has(vehicleId)) {
            const dismissCallback = dismissCallbacks.get(vehicleId);
            if (dismissCallback) {
              dismissCallback();
              dismissCallbacks.delete(vehicleId);
            }
          }
        });

        nextFailureModes.forEach((nextMode, vehicleId) => {
          const previousMode = prevFailureModes.get(vehicleId);

          // Skip if this is the first time seeing this vehicle or if the mode hasn't changed
          if (previousMode === undefined || previousMode === nextMode) return;

          const fleetVisibility = useFleetVisibilityStore.getState().visibility;
          if (
            fleetVisibility === 'mine' &&
            !selectIsVehicleUnderMyControl(useGcsStore.getState().data, gcsId, vehicleId)
          ) {
            return;
          }

          const liveMission = gcsData?.liveMissionsMap.get(vehicleId);
          const untilMs = liveMission
            ? gcsData?.missionDetailedStateMap.get(liveMission.missionId)?.untilMs
            : undefined;

          // Handle vehicle reconnection when leaving UNKNOWN_VEHICLE_DISCONNECTED
          if (previousMode === MissionFailureMode.UNKNOWN_VEHICLE_DISCONNECTED) {
            soundEffectsService.enqueue(failureModeExitVehicleReconnectedSound);

            showNotification({
              type: InAppNotificationType.SUCCESS,
              title: `${getShortVehicleId(vehicleId)}: ${en.missionFailureModes.vehicleReconnected}`,
              durationMs: DEFAULT_NOTIFICATION_DURATION_MS,
              onClick: () => selectVehicle(vehicleId, gcsId)
            });
          }

          // Notify when transitioning to a non-nominal failure mode
          if (nextMode !== MissionFailureMode.NOMINAL) {
            // Dismiss previous notification before showing new one
            const dismissCallback = dismissCallbacks.get(vehicleId);
            if (dismissCallback) {
              dismissCallback();
            }

            const sound = getFailureModeSound(nextMode);

            if (sound) {
              soundEffectsService.enqueue(sound);
            }

            if (nextMode === MissionFailureMode.SAFED_UP && untilMs) {
              const dismiss = showNotification({
                type: InAppNotificationType.ERROR,
                title: `${getShortVehicleId(vehicleId)}: ${getFailureModeDisplayName(nextMode)}`,
                durationMs: Math.max(0, untilMs - Date.now()),
                bottomComponent: <Countdown targetMs={untilMs} label={en.missionFailureModes.landingIn} />,
                onClick: () => selectVehicle(vehicleId, gcsId)
              });
              dismissCallbacks.set(vehicleId, dismiss);
            } else {
              const dismiss = showNotification({
                type: InAppNotificationType.ERROR,
                title: `${getShortVehicleId(vehicleId)}: ${getFailureModeDisplayName(nextMode)}`,
                durationMs: DEFAULT_NOTIFICATION_DURATION_MS,
                onClick: () => selectVehicle(vehicleId, gcsId)
              });
              dismissCallbacks.set(vehicleId, dismiss);
            }
          } else {
            // Transitioning to NOMINAL - dismiss previous notification
            const dismissCallback = dismissCallbacks.get(vehicleId);
            if (dismissCallback) {
              dismissCallback();
              dismissCallbacks.delete(vehicleId);
            }
          }
        });
      },
      {
        fireImmediately: true,
        equalityFn
      }
    );

    return () => {
      unsubscribe();
      // Dismiss all active notifications on cleanup
      dismissCallbacks.forEach((dismiss) => dismiss());
      dismissCallbacks.clear();
    };
  }, [gcsId]);

  return null;
}

function getFailureModeSound(failureMode: MissionFailureMode): string | null {
  switch (failureMode) {
    case MissionFailureMode.RTL:
    case MissionFailureMode.FC_RTL:
      return failureModeRtlSound;
    case MissionFailureMode.SAFING_UP:
      return failureModeSafeUpSound;
    case MissionFailureMode.DESCENDING:
      return failureModeDescendingSound;
    case MissionFailureMode.EMERGENCY_LAND:
      return failureModeEmergencyLandingSound;
    case MissionFailureMode.FC_LOSS:
      return failureModeFcLossSound;
    case MissionFailureMode.FTS_TRIGGERED:
      return failureModeFtsTriggeredSound;
    case MissionFailureMode.UNKNOWN_VEHICLE_DISCONNECTED:
      return failureModeUnknownVehicleDisconnectedSound;
    case MissionFailureMode.NOMINAL:
    case MissionFailureMode.SAFED_UP:
    case MissionFailureMode.DESCENDED:
    default:
      return null;
  }
}

function getFailureModeDisplayName(failureMode: MissionFailureMode): string {
  switch (failureMode) {
    case MissionFailureMode.RTL:
      return en.missionFailureModes.rtl;
    case MissionFailureMode.SAFING_UP:
      return en.missionFailureModes.safingUp;
    case MissionFailureMode.SAFED_UP:
      return en.missionFailureModes.safedUp;
    case MissionFailureMode.DESCENDING:
      return en.missionFailureModes.descending;
    case MissionFailureMode.DESCENDED:
      return en.missionFailureModes.descended;
    case MissionFailureMode.EMERGENCY_LAND:
      return en.missionFailureModes.emergencyLand;
    case MissionFailureMode.FC_RTL:
      return en.missionFailureModes.fcRtl;
    case MissionFailureMode.FC_LOSS:
      return en.missionFailureModes.fcLoss;
    case MissionFailureMode.FTS_TRIGGERED:
      return en.missionFailureModes.ftsTriggered;
    case MissionFailureMode.UNKNOWN_VEHICLE_DISCONNECTED:
      return en.missionFailureModes.unknownVehicleDisconnected;
    default:
      return failureMode;
  }
}
